import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, FormControlLabel, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import api from '../../services/api';
import CompanyTransferCompletePanel from './CompanyTransferCompletePanel';

const names = { missing: 'Por copiar', identical: 'Ya existe igual', conflict: 'Conflicto: no se modifica', inserted: 'Copiada y verificada' };
export default function CompanyTransferExecutionPanel(props) {
  return process.env.REACT_APP_COMPANY_TRANSFER_V2_ENABLED === 'true' ? <CompanyTransferCompletePanel {...props} /> : <ResolutionTransferPanel {...props} />;
}
function ResolutionTransferPanel({ customer, originId, destinationId, selection, sources }) {
  const [job, setJob] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const mounted = useRef(true);
  const base = `/clients/${customer.id}/existing-api-registration`;
  useEffect(() => {
    mounted.current = true;
    api.get(`${base}/copy-job`).then(({ data }) => {
      if (mounted.current) { setJob(data.job); setEnabled(data.executionEnabled === true); }
    }).catch(failure => { if (mounted.current) setError(failure.response?.data?.message || 'No se pudo recuperar el ultimo lote.'); });
    return () => { mounted.current = false; };
  }, [base]);
  useEffect(() => { setAgreed(false); }, [originId, destinationId, selection]);
  const matches = job && String(job.originId) === String(originId) && String(job.destinationId) === String(destinationId);
  const onlyResolutions = selection.length === 1 && selection[0] === 'resolutions';
  const prepare = async () => {
    setBusy(true); setError(''); setAgreed(false);
    try {
      const { data } = await api.post(`${base}/copy-prepare`, { originId, destinationId, selection }, { timeout: 90000 });
      if (mounted.current) setJob(data.job);
    } catch (failure) { if (mounted.current) setError(failure.response?.data?.message || 'No se pudo preparar. No se ha solicitado copiar.'); }
    finally { if (mounted.current) setBusy(false); }
  };
  const run = async () => {
    setBusy(true); setError('');
    let current = job;
    try {
      while (mounted.current) {
        const { data } = await api.post(`${base}/copy-step`, { jobId: current.id, cursor: current.cursor, confirm: true, acknowledgeCounters: true }, { timeout: 100000 });
        if (!mounted.current) break;
        const next = data.job;
        if (!next || next.id !== current.id) throw new Error('Lote no confirmado');
        setJob(next);
        if (['completed', 'completed_with_conflicts', 'retryable'].includes(next.status)) break;
        if (next.cursor <= current.cursor) throw new Error('Avance no confirmado');
        current = next;
      }
    } catch (failure) { if (mounted.current) setError(failure.response?.data?.message || 'Se interrumpio la respuesta. Reanude el mismo lote: se comprobara lo ya copiado antes de insertar.'); }
    finally { if (mounted.current) { setBusy(false); setAgreed(false); } }
  };
  const sourceName = id => sources.find(s => String(s.id) === String(id))?.name || `API ${id}`;
  return <section aria-label="Copia aditiva" style={{ marginTop: 24 }}>
    <Typography variant="h6">Copia aditiva de resoluciones</Typography>
    <Alert severity="warning">Ejecucion disponible solo para resoluciones y prefijos, en una empresa ya creada. No incluye planes, consecutivos, historicos ni archivos. No emita documentos hasta revisar la numeracion y el corte operativo. Los otros checks solo se comparan.</Alert>
    {error && <Alert severity="error">{error}</Alert>}
    {!enabled && <Typography>La ejecucion no esta habilitada para esta sesion o entorno.</Typography>}
    <Button onClick={prepare} disabled={busy || !enabled || !onlyResolutions || !originId || !destinationId || originId === destinationId}>Preparar copia de resoluciones</Button>
    {job && <>
      <Typography>Lote #{job.id}: {sourceName(job.originId)} → {sourceName(job.destinationId)} · NIT {job.nit} · Revisadas {job.cursor} de {job.total}</Typography>
      {job.entries.map(entry => {
        const result = job.results.find(r => r.key === entry.key);
        return <Typography key={entry.key} variant="body2">{entry.label}: {names[result?.status || entry.preview] || 'Por revisar'}</Typography>;
      })}
      {job.status === 'retryable' && <Alert severity="warning">Respuesta no confirmada. Puede reanudar: el destino se compara de nuevo sin sobrescribir.</Alert>}
      {['completed', 'completed_with_conflicts'].includes(job.status) ? <Alert severity={job.status === 'completed' ? 'success' : 'warning'}>
        {job.status === 'completed' ? 'Lote de resoluciones revisado por completo.' : 'Lote revisado con conflictos pendientes; no se sobrescribieron.'} Revise consecutivos antes de emitir. Puede preparar otro lote para completar lo faltante.
      </Alert> : <>
        {!matches && <Typography>Seleccione las APIs de este lote para retomarlo.</Typography>}
        <FormControlLabel label="Confirmo copiar solo lo faltante, sin sobrescribir. Revisare los consecutivos antes de emitir." control={<Checkbox checked={agreed} disabled={busy || !matches || !onlyResolutions} onChange={e => setAgreed(e.target.checked)} />} />
        <Button color="primary" variant="contained" disabled={busy || !enabled || !matches || !onlyResolutions || !agreed} onClick={run}>{job.status === 'prepared' ? 'Copiar resoluciones faltantes' : 'Reanudar lote'}</Button>
      </>}
    </>}
    {busy && <Typography role="status">Procesando resoluciones. Si cierra la ventana, el avance confirmado queda guardado para retomarlo.</Typography>}
  </section>;
}
